import { firestoreAction } from "@/store/firestoreBinding";
import { db } from "@/firebase";

const partnersRef = db.collection("partners");

function sortPartners(partners) {
  return [...partners].sort((a, b) => {
    const aHasOrder = typeof a.order === "number";
    const bHasOrder = typeof b.order === "number";

    if (aHasOrder && bHasOrder && a.order !== b.order) {
      return a.order - b.order;
    }

    if (aHasOrder && !bHasOrder) {
      return -1;
    }

    if (!aHasOrder && bHasOrder) {
      return 1;
    }

    return (a.name || "").localeCompare(b.name || "");
  });
}

export const PartnersService = {
  namespaced: true, // Ensure namespacing is enabled
  state: {
    partners: [],
  },
  getters: {
    partners: (state) => state.partners,
  },
  actions: {
    bindPartners: firestoreAction(({ bindFirestoreRef }) => {
      return bindFirestoreRef("partners", partnersRef, {
        serialize: (doc) => ({ ...doc.data(), id: doc.id }),
      });
    }),
    async fetchPartners({ commit }) {
      try {
        const snapshot = await partnersRef.get();
        const partners = [];
        snapshot.forEach((doc) => {
          partners.push({ ...doc.data(), id: doc.id });
        });
        commit("setPartners", sortPartners(partners));
      } catch (error) {
        console.error("Error fetching partners:", error);
      }
    },
    async getPartner(_context, id) {
      if (!id || typeof id !== "string" || id.trim() === "") {
        throw new Error("Invalid partner id");
      }
      const snapshot = await partnersRef.doc(id).get();
      if (!snapshot.exists) {
        console.log("No such document!");
        return;
      }
      return snapshot.data();
    },
    async findPartnersByName(_context, partnerName) {
      const snapshot = await partnersRef.where("name", "==", partnerName).get();
      if (snapshot.empty) {
        console.log("No matching documents.");
        return;
      }
      return snapshot;
    },
    async addPartner(_context, partner) {
      const partnerData = { ...partner };
      delete partnerData.id;

      // Firestore orderBy("order") excludes documents missing the field.
      if (typeof partnerData.order !== "number") {
        const snapshot = await partnersRef.get();
        let maxOrder = 0;
        snapshot.forEach((doc) => {
          const order = doc.data().order;
          if (typeof order === "number" && order > maxOrder) {
            maxOrder = order;
          }
        });
        partnerData.order = maxOrder + 1;
      }

      const result = await partnersRef.add(partnerData);
      return result.id;
    },
    async updatePartner(_context, partner) {
      if (!partner || !partner.id || typeof partner.id !== "string" || partner.id.trim() === "") {
        throw new Error("Invalid partner id");
      }
      const partnerRefUpdate = partnersRef.doc(partner.id);
      if (!partnerRefUpdate) {
        console.log("No such document!");
        return;
      }
      const { id, ...partnerData } = partner;
      await partnerRefUpdate.update(partnerData);
    },
    async deletePartner(_context, partnerId) {
      if (!partnerId || typeof partnerId !== "string" || partnerId.trim() === "") {
        throw new Error("Invalid partner id");
      }
      const partnerRefDelete = partnersRef.doc(partnerId);
      await partnerRefDelete.delete();
    },
    async updatePartnerOrder(_context, partners) {
      const batch = db.batch();
      partners.forEach((partner) => {
        if (!partner.id || typeof partner.id !== "string" || partner.id.trim() === "") {
          return;
        }
        const partnerRef = partnersRef.doc(partner.id);
        batch.update(partnerRef, { order: partner.order });
      });
      await batch.commit();
    },
    async logPartnerClick(context, payload) {
      const { partnerId } = payload;
      let { userInfo } = payload;

      try {
        if (!partnerId || typeof partnerId !== "string" || partnerId.trim() === "") {
          throw new Error("Invalid partnerId: Must be a non-empty string");
        }

        if (typeof userInfo === "string") {
          try {
            userInfo = JSON.parse(userInfo);
          } catch {
            throw new Error("Invalid userInfo: Failed to parse JSON string");
          }
        }

        if (!userInfo || typeof userInfo !== "object" || !userInfo.id || !userInfo.name) {
          throw new Error("Invalid userInfo: Must be an object with 'id' and 'name' properties");
        }

        const logRef = partnersRef.doc(partnerId).collection("clickLogs");
        await logRef.add({
          timestamp: new Date(),
          user: userInfo,
        });
      } catch (error) {
        console.error("Error in logPartnerClick:", error.message); // Log the error
        throw error; // Re-throw the error to propagate it to the caller
      }
    },
  },
  mutations: {
    setPartners(state, partners) {
      state.partners = sortPartners(partners);
    },
  },
};
